import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import TextInput from "../ui/TextInput";
import Dropdown from "../common/Dropdown";
import { Tooltip } from "@mui/material";
import { getSoundLibraryAI, getSoundLibraryAITypes, getFilteredSoundLibraryAICategories } from "../../api/soundLibrary";
import { SoundLibraryAI } from "../../types/types";
import { SoundLibraryAITypeResponse, SoundLibraryAICategoryResponse } from "../../types/requestsOutputs";
import AudioPlayerRewind from "../common/AudioPlayer";
import { useLanguage } from "../../context/LanguageContext";
import { useNotification } from "../../context/NotificationContext";
import Icon from "../common/Icon";
import styles from "../../pages/create/SoundLibrary.module.css";

const SoundLibraryList: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { language } = useLanguage();
    const { showNotification } = useNotification();

    const [library, setLibrary] = useState<SoundLibraryAI[]>([]);
    const [types, setTypes] = useState<SoundLibraryAITypeResponse[]>([]);
    const [categories, setCategories] = useState<SoundLibraryAICategoryResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [typesLoading, setTypesLoading] = useState<boolean>(true);
    const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);
    const [deleteLoading] = useState<string | null>(null);

    const [search, setSearch] = useState<string>("");
    const [searchDebounced, setSearchDebounced] = useState<string>("");
    const [selectedType, setSelectedType] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [selectedSound, setSelectedSound] = useState<SoundLibraryAI | null>(null);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState<number>(1);

    const [page, setPage] = useState<number>(1);
    const [limit] = useState<number>(10);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounced(search);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    const fetchLibrary = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token") || "";
            const response = await getSoundLibraryAI({
                token,
                language,
                searchQuery: searchDebounced,
                includeRewind: true,
                typeId: selectedType,
                categoryId: selectedCategory,
                page,
                limit,
            });

            const { items, total, totalPages } = response;

            setTotalItems(total);
            setTotalPages(totalPages);
            setLibrary(items);
        } catch (error) {
            console.error("Error fetching sound library:", error);
            showNotification({
                message: t("errorFetchingSoundLibrary"),
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    }, [language, selectedType, selectedCategory, searchDebounced, showNotification, t, page, limit]);

    useEffect(() => {
        setPage(1);
        setSelectedSound(null);
    }, [searchDebounced, selectedType, selectedCategory]);

    const fetchCategories = useCallback(async () => {
        try {
            setCategoriesLoading(true);
            const token = localStorage.getItem("token") || "";
            const filteredCategories = await getFilteredSoundLibraryAICategories({
                token,
                language,
                includeRewind: true,
                searchQuery: searchDebounced,
                typeId: selectedType || null,
            });
            setCategories(filteredCategories);
        } catch (err) {
            console.error("Failed to fetch categories", err);
            showNotification({
                message: t("errorFetchingSoundCategories"),
                type: "error",
            });
        } finally {
            setCategoriesLoading(false);
        }
    }, [language, searchDebounced, selectedType, showNotification, t]);

    useEffect(() => {
        const token = localStorage.getItem("token") || "";

        setTypesLoading(true);
        getSoundLibraryAITypes({ token, language })
            .then(setTypes)
            .catch((err) => {
                console.error("Failed to fetch types", err);
                showNotification({
                    message: t("errorFetchingSoundTypes"),
                    type: "error",
                });
            })
            .finally(() => setTypesLoading(false));
    }, [language, showNotification, t]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        fetchLibrary();
    }, [fetchLibrary]);

    const visiblePages = useMemo(() => {
        const range = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) range.push(i);
        } else {
            range.push(1);
            if (page > 3) range.push("...");
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
                range.push(i);
            }
            if (page < totalPages - 2) range.push("...");
            range.push(totalPages);
        }

        return range;
    }, [page, totalPages]);

    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, totalItems);

    const typeOptions = useMemo(
        () =>
            types.map((type) => ({
                value: type.id,
                label: type.localizedName,
            })),
        [types],
    );

    const categoryOptions = useMemo(
        () => categories.map((cat) => ({ value: cat.id, label: cat.localizedName })),
        [categories],
    );

    useEffect(() => {
        if (selectedCategory && !categoryOptions.some((option) => option.value === selectedCategory)) {
            setSelectedCategory("");
        }
    }, [selectedCategory, categoryOptions]);

    return (
        <>
            <div className={styles.filters}>
                <div className={styles.filtersLbl}>{t("filterSounds")}</div>
                <div className={styles.filtersRow}>
                    <div className={`${styles.search} ${styles.searchWrap}`}>
                        <span className={styles.searchIcon} aria-hidden>
                            <Icon name="ic-search" />
                        </span>
                        <TextInput
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("searchByNameOrDescription")}
                        />
                    </div>
                    <Dropdown
                        label=""
                        options={typeOptions}
                        value={selectedType}
                        onChange={setSelectedType}
                        placeholder={t("filterByType")}
                        loading={typesLoading}
                        error={false}
                        isWithoutMargin
                    />
                    <Dropdown
                        label=""
                        options={categoryOptions}
                        value={selectedCategory}
                        onChange={setSelectedCategory}
                        placeholder={t("filterByCategory")}
                        loading={categoriesLoading}
                        error={false}
                        isWithoutMargin
                    />
                </div>
            </div>

            {loading || deleteLoading ? (
                <div className={styles.loading}>{t("loading") || "Loading…"}</div>
            ) : library.length === 0 ? (
                <div className={styles.empty}>{t("noSoundsFound")}</div>
            ) : (
                <>
                    <div className={styles.list}>
                        {library.map((item, index) => (
                            <div key={item.id || index} className={styles.snd}>
                                <button
                                    type="button"
                                    className={styles.play}
                                    onClick={() => setSelectedSound(item)}
                                    aria-label={t("play") || "Play"}
                                >
                                    <Icon name="ic-play" />
                                </button>
                                <div className={styles.nameCell}>
                                    <Tooltip title={`${t("name")}: ${item.name}`} placement="bottom-start">
                                        <h4 className={styles.name}>{item.name}</h4>
                                    </Tooltip>
                                    <Tooltip
                                        title={`${t("description")}: ${item.soundDescription}`}
                                        placement="bottom-start"
                                    >
                                        <div className={styles.sub}>{item.soundDescription}</div>
                                    </Tooltip>
                                </div>
                                <Tooltip title={`${t("type")}: ${item.type}`} placement="bottom-start">
                                    <div className={styles.type}>{item.type}</div>
                                </Tooltip>
                                <div className={styles.cats}>
                                    {(item.categories || []).map((c) => (
                                        <span key={c.id || c.name} className={styles.cat}>
                                            {c.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    {selectedSound && (
                        <div className={styles.player}>
                            <AudioPlayerRewind
                                title={selectedSound.name}
                                src={selectedSound.url}
                                showAddtoProject={false}
                                isAutoPlay={true}
                                hideSeparator
                                embedded
                            />
                        </div>
                    )}
                    <div className={styles.pagination}>
                        <button
                            type="button"
                            className={styles.pg}
                            disabled={page === 1}
                            onClick={() => setPage((p) => p - 1)}
                            aria-label="Previous page"
                        >
                            ‹
                        </button>

                        {visiblePages.map((p, idx) =>
                            p === "..." ? (
                                <span key={`ellipsis-${idx}`} className={`${styles.pg} ${styles.pgDots}`}>
                                    …
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    key={p}
                                    className={`${styles.pg}${p === page ? ` ${styles.pgActive}` : ""}`}
                                    onClick={() => setPage(p as number)}
                                >
                                    {p}
                                </button>
                            ),
                        )}

                        <button
                            type="button"
                            className={styles.pg}
                            disabled={page === totalPages}
                            onClick={() => setPage((p) => p + 1)}
                            aria-label="Next page"
                        >
                            ›
                        </button>

                        <span className={styles.paginationTotal}>
                            {t("paginationCounter")}: {`${startItem}–${endItem} / ${totalItems}`}
                        </span>
                    </div>
                </>
            )}
        </>
    );
};

export default SoundLibraryList;
